import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon
} from "@chakra-ui/react";
import { FaFlask, FaTint, FaArrowRight, FaCheckCircle } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

const WaterPurifiersHub: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/water-purifiers"
        fallbackTitle="Water Purifiers for Australian Homes — One Water Australia"
        fallbackDescription="When filtration isn't enough — multi-stage water purification systems for Australian homes. Understand filter vs purifier."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 16, md: 24 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1548839140-29a749e1cf4d?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center" maxW="3xl" mx="auto">
            <Icon as={FaFlask} boxSize={12} color={WATER_BLUE} />
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              Water purifiers for Australian homes.
            </Heading>
            <Text fontSize="xl" color="whiteAlpha.800">
              When filtration isn't enough.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={WATER_BLUE}
              color={NAVY}
              size="lg"
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#8DC5E0" }}
              transition="all 0.2s"
            >
              Book a Free Water Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Filter vs Purifier */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={4} mb={14} textAlign="center">
          <Heading size="xl" color={NAVY}>Filter vs Purifier — What's the Difference?</Heading>
          <Text fontSize="xl" color={DEEP_AQUA} fontWeight="600">
            "A filter improves your water. A purifier transforms it."
          </Text>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Purifiers use multiple treatment stages — sediment, carbon, membrane or RO, UV, and remineralisation — to address a broader and more demanding set of water quality issues.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          <Box p={8} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
            <Icon as={FaTint} boxSize={10} color={WATER_BLUE} mb={4} />
            <Heading size="md" color={NAVY} mb={3}>Water Filter</Heading>
            <Text color="gray.600" mb={4} fontSize="sm">
              Addresses chlorine, sediment, taste, and odour. Typically one or two treatment stages. Suitable for improving mains-supplied water.
            </Text>
            <VStack align="flex-start" spacing={2}>
              {["Removes chlorine and taste issues", "Removes sediment", "Suitable for most Australian mains water", "Lower cost entry point"].map((b) => (
                <HStack key={b} spacing={2}>
                  <Icon as={FaCheckCircle} color={WATER_BLUE} boxSize={3} />
                  <Text fontSize="sm" color="gray.600">{b}</Text>
                </HStack>
              ))}
            </VStack>
          </Box>

          <Box p={8} bg={ICE_MIST} borderRadius="xl" border={`2px solid ${WATER_BLUE}40`}>
            <Icon as={FaFlask} boxSize={10} color={DEEP_AQUA} mb={4} />
            <Heading size="md" color={NAVY} mb={3}>Water Purifier</Heading>
            <Text color="gray.600" mb={4} fontSize="sm">
              Multi-stage treatment addressing dissolved solids, heavy metals, biological contaminants, fluoride, and chemicals. For higher-demand situations.
            </Text>
            <VStack align="flex-start" spacing={2}>
              {["Removes dissolved solids and metals", "Addresses fluoride and nitrates", "Biological protection with UV stage", "Remineralisation stage available", "Bore water and tank water capable"].map((b) => (
                <HStack key={b} spacing={2}>
                  <Icon as={FaCheckCircle} color={DEEP_AQUA} boxSize={3} />
                  <Text fontSize="sm" color="gray.600">{b}</Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Product categories */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Heading size="xl" color={NAVY}>Purifier Options</Heading>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            <Box
              bg="white"
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              _hover={{ shadow: "lg", borderColor: WATER_BLUE }}
              transition="all 0.3s"
              overflow="hidden"
            >
              <Box h="220px" overflow="hidden">
                <Box
                  as="img"
                  src="https://images.unsplash.com/photo-1621905251918-48416bd8575a?auto=format&fit=crop&w=800&q=80"
                  alt="Under-sink water purifier system installation"
                  w="full"
                  h="full"
                  objectFit="cover"
                />
              </Box>
              <Box p={10}>
                <Text fontSize="xs" fontWeight="700" color={WATER_BLUE} textTransform="uppercase" letterSpacing="wider" mb={3}>Best for</Text>
                <Text color={DEEP_AQUA} fontWeight="600" mb={4} fontSize="sm">Drinking and cooking water</Text>
                <Heading size="lg" color={NAVY} mb={3}>Under-Sink Purifier</Heading>
                <Text color="gray.600" mb={6} fontSize="sm">
                  Multi-stage treatment at your kitchen tap — sediment, carbon, reverse osmosis membrane, and UV polish. Produces the highest purity point-of-use water available for a residential setting.
                </Text>
                <Button
                  as={RouterLink}
                  to="/water/water-purifiers/under-sink-system"
                  bg={NAVY}
                  color="white"
                  rightIcon={<FaArrowRight />}
                  _hover={{ bg: DEEP_AQUA }}
                  transition="all 0.2s"
                >
                  Under-Sink Purifier
                </Button>
              </Box>
            </Box>

            <Box
              bg="white"
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              _hover={{ shadow: "lg", borderColor: WATER_BLUE }}
              transition="all 0.3s"
              overflow="hidden"
            >
              <Box h="220px" overflow="hidden">
                <Box
                  as="img"
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800&q=80"
                  alt="Modern Australian home with whole-home water purification"
                  w="full"
                  h="full"
                  objectFit="cover"
                />
              </Box>
              <Box p={10}>
                <Text fontSize="xs" fontWeight="700" color={WATER_BLUE} textTransform="uppercase" letterSpacing="wider" mb={3}>Best for</Text>
                <Text color={DEEP_AQUA} fontWeight="600" mb={4} fontSize="sm">Whole property treatment</Text>
                <Heading size="lg" color={NAVY} mb={3}>Whole-Home Purifier</Heading>
                <Text color="gray.600" mb={6} fontSize="sm">
                  Entry-point treatment for the entire property. Sediment, carbon, and UV stages protect every tap, shower, and appliance. Suited to bore water, tank water, or properties with broad water quality concerns.
                </Text>
                <Button
                  variant="outline"
                  borderColor={NAVY}
                  color={NAVY}
                  rightIcon={<FaArrowRight />}
                  _hover={{ bg: NAVY, color: "white" }}
                  transition="all 0.2s"
                >
                  Whole-Home Purifier
                </Button>
              </Box>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* When purifier vs filter */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={4} mb={12} textAlign="center">
          <Heading size="xl" color={NAVY}>When Do You Need a Purifier vs a Filter?</Heading>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          <Box>
            <Heading size="md" color={NAVY} mb={6}>A filter is likely sufficient if:</Heading>
            <VStack align="flex-start" spacing={3}>
              {[
                "You're on mains water and the primary concern is chlorine, taste, or odour",
                "You want filtered water at one or two taps",
                "Your water quality results are within normal Australian ranges",
                "You don't have specific heavy metal or dissolved solid concerns",
              ].map((item) => (
                <HStack key={item} spacing={3} align="flex-start">
                  <Icon as={FaCheckCircle} color={WATER_BLUE} boxSize={4} mt={0.5} flexShrink={0} />
                  <Text fontSize="sm" color="gray.600">{item}</Text>
                </HStack>
              ))}
            </VStack>
          </Box>
          <Box>
            <Heading size="md" color={NAVY} mb={6}>A purifier makes more sense if:</Heading>
            <VStack align="flex-start" spacing={3}>
              {[
                "You're on bore water, tank water, or rainwater",
                "You want the highest possible drinking water purity",
                "Your water test shows elevated TDS, heavy metals, fluoride, or nitrates",
                "You have specific health-driven concerns about water quality",
                "Your property has ageing infrastructure",
              ].map((item) => (
                <HStack key={item} spacing={3} align="flex-start">
                  <Icon as={FaCheckCircle} color={DEEP_AQUA} boxSize={4} mt={0.5} flexShrink={0} />
                  <Text fontSize="sm" color="gray.600">{item}</Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>

      {/* CTA */}
      <Box bg={NAVY} py={16}>
        <Container maxW="container.xl" textAlign="center">
          <VStack spacing={5}>
            <Heading size="xl" color="white">Not sure which applies to you?</Heading>
            <Text fontSize="lg" color="whiteAlpha.800" maxW="xl">
              A water assessment gives you the data to decide. Book a free assessment — whether that results in a purifier, a filter, or no system at all, you'll know exactly where you stand.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={WATER_BLUE}
              color={NAVY}
              size="lg"
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#8DC5E0" }}
              transition="all 0.2s"
            >
              Book a Free Water Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterPurifiersHub;
